import { useEffect, useState } from 'react';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { BottomNav } from './components/BottomNav';
import { useBootstrap, useUser } from './lib/auth';
import { getTelegram, initTelegram } from './lib/telegram';
import { About } from './screens/About';
import { Admin } from './screens/Admin';
import { Onboarding } from './screens/Onboarding';
import { Privacy } from './screens/Privacy';
import { Terms } from './screens/Terms';
import { BracketBuilder } from './screens/BracketBuilder';
import { BracketConfirmed } from './screens/BracketConfirmed';
import { BracketHub } from './screens/BracketHub';
import { CreateLeague } from './screens/CreateLeague';
import { DevLogin } from './screens/DevLogin';
import { Home } from './screens/Home';
import { LeagueDetail } from './screens/LeagueDetail';
import { Leagues } from './screens/Leagues';
import { PredictionSheet } from './screens/PredictionSheet';
import { Profile } from './screens/Profile';
import { Settings } from './screens/Settings';
import { Today } from './screens/Today';

export default function App() {
  const { state, error } = useBootstrap();
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    initTelegram();
  }, []);

  // Back-button integration
  useEffect(() => {
    const tg = getTelegram();
    if (!tg) return;
    const onBack = () => nav(-1);
    if (location.pathname !== '/') {
      tg.BackButton.show();
      tg.BackButton.onClick(onBack);
    } else {
      tg.BackButton.hide();
    }
    return () => tg.BackButton.offClick(onBack);
  }, [location.pathname, nav]);

  if (state === 'loading') {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="font-display text-2xl text-trophy-gold animate-pulse">PunditXI</div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="mx-auto max-w-md px-4 pt-10">
        <div className="font-display text-2xl uppercase">Something went wrong</div>
        <p className="text-text-dim text-sm mt-2">{error}</p>
      </div>
    );
  }

  if (state === 'needs-dev-login') {
    return <DevLogin />;
  }

  return (
    <>
      <OnboardingGate />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/today" element={<Today />} />
        <Route path="/fixture/:id" element={<PredictionSheet />} />
        <Route path="/bracket" element={<BracketHub />} />
        <Route path="/bracket/builder" element={<BracketBuilder />} />
        <Route path="/bracket/confirmed" element={<BracketConfirmed />} />
        <Route path="/leagues" element={<Leagues />} />
        <Route path="/leagues/new" element={<CreateLeague />} />
        <Route path="/leagues/:slug" element={<LeagueDetail />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/about" element={<About />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/admin" element={<Admin />} />
        <Route
          path="*"
          element={
            <div className="mx-auto max-w-md px-4 pt-10 space-y-3">
              <div className="font-display text-3xl uppercase">Not found</div>
              <Link to="/" className="font-display-alt text-xs uppercase tracking-widest text-trophy-gold">
                Back home →
              </Link>
            </div>
          }
        />
      </Routes>
      <BottomNav />
    </>
  );
}

function OnboardingGate() {
  const user = useUser();
  const [dismissed, setDismissed] = useState(false);
  // Show only when we have a user, they haven't onboarded yet, and they
  // haven't dismissed it this session.
  if (!user.data) return null;
  if (user.data.onboardedAt) return null;
  if (dismissed) return null;
  return (
    <div className="fixed inset-0 z-40 bg-bg-night overflow-y-auto">
      <Onboarding onDone={() => setDismissed(true)} />
    </div>
  );
}
